/**
 * AdminBarHeightController - Reactive Controller für die Höhe der WP Admin-Bar
 *
 * Ermittelt die Höhe von #wpadminbar, wenn der body die Klasse `admin-bar` hat,
 * und hält diese Höhe per Callback aktuell (inkl. Änderungen bei Bildschirmbreite).
 *
 * @author jjarolim
 */

import type { ReactiveController, ReactiveControllerHost } from 'lit'

export class AdminBarHeightController implements ReactiveController {
    private _host: ReactiveControllerHost
    private _callback: ((height: number) => void) | null = null
    private _adminBar: HTMLElement | null = null
    private _observer: ResizeObserver | null = null
    private _currentHeight: number = 0

    constructor(
        host: ReactiveControllerHost,
        callback?: (height: number) => void
    ) {
        this._host = host
        this._callback = callback ?? null
        host.addController(this)
    }

    get height(): number {
        return this._currentHeight
    }

    hostConnected(): void {
        this._setup()
    }

    hostDisconnected(): void {
        this._cleanup()
    }

    hostUpdate(): void {
        // Wird vor jedem Update aufgerufen
    }

    hostUpdated(): void {
        // Wird nach jedem Update aufgerufen
        // Falls beim ersten Rendern noch kein Admin-Bar Element gefunden wurde,
        // versuchen wir es erneut.
        if (!this._adminBar && document?.body?.classList.contains('admin-bar')) {
            this._setup()
        }
    }

    private _setup(): void {
        if (typeof document === 'undefined') {
            return
        }

        // Nur aktiv, wenn die Admin-Bar-Klasse gesetzt ist
        if (!document.body.classList.contains('admin-bar')) {
            this._updateHeight(0)
            this._cleanup()
            return
        }

        const adminBar = document.getElementById('wpadminbar')

        if (!adminBar) {
            this._updateHeight(0)
            this._cleanup()
            return
        }

        this._adminBar = adminBar

        // Initiale Höhe ermitteln
        this._measureAndUpdate()

        // ResizeObserver für laufende Änderungen (inkl. Breakpoint-Wechsel)
        if ('ResizeObserver' in window) {
            if (!this._observer) {
                this._observer = new ResizeObserver(() => {
                    this._measureAndUpdate()
                })
            }

            this._observer.observe(this._adminBar)
        }
    }

    private _measureAndUpdate(): void {
        if (!this._adminBar) {
            this._updateHeight(0)
            return
        }

        const newHeight = this._adminBar.offsetHeight || 0
        this._updateHeight(newHeight)
    }

    private _updateHeight(newHeight: number): void {
        if (this._currentHeight === newHeight) {
            return
        }

        this._currentHeight = newHeight

        if (this._callback) {
            this._callback(newHeight)
        }

        this._host.requestUpdate()
    }

    private _cleanup(): void {
        if (this._observer && this._adminBar) {
            this._observer.unobserve(this._adminBar)
        }

        if (this._observer) {
            this._observer.disconnect()
            this._observer = null
        }

        this._adminBar = null
    }
}

